import { useEffect, useMemo, useRef, useState, type CSSProperties } from "react";
import {
  Activity,
  Flame,
  Maximize2,
  TrendingDown,
  TrendingUp,
  Zap,
} from "lucide-react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ComposedChart,
  Line,
  PolarAngleAxis,
  RadialBar,
  RadialBarChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import { Button } from "@/shared/ui/button";
import { Card, CardContent } from "@/shared/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/shared/ui/dialog";
import { Skeleton, SkeletonChart, SkeletonStyles, SkeletonText } from "@/shared/ui/skeleton";
import { useAppContext } from "@/app/providers/AppContext";
import { formatUpdatedTimestamp, formatTime, todayInTz } from "@/shared/lib/timezone";
import type { ApiFn } from "@/shared/lib/types";

const GREEN = "hsl(160, 84%, 39%)";
const RED = "hsl(0, 84%, 60%)";
const AMBER = "hsl(38, 92%, 50%)";
const ACCENT = "var(--primary)";
const BORDER = "var(--border)";
const MUTED = "var(--muted-foreground)";

interface TopFlow {
  ticker: string;
  type: string;
  strike: number;
  premium: number;
  size: number;
  is_sweep: boolean;
}

interface Summary {
  symbol: string;
  date: string;
  status?: string;
  underlying_price: number;
  cards?: {
    sentiment: string;
    sentiment_value: number;
    put_call_ratio: number;
    put_call_ratio_oi: number;
    call_flow_usd: number;
    put_flow_usd: number;
    sweeps_pct: number;
    top_flow: TopFlow[];
  };
  gex_profile?: { strike: number; gex: number }[];
  net_gex?: number;
  zero_gamma_strike?: number | null;
  is_approximation?: boolean;
  ai_summary?: string;
  updated_at?: string | null;
}

interface Bucket {
  bucket_ts: string;
  cum_call_prem: number;
  cum_put_prem: number;
  net_gex: number;
  underlying_price: number;
}

interface Intraday {
  symbol: string;
  date: string;
  buckets: Bucket[];
}



function fmtCompact(value: number, signed = false): string {
  if (value == null || !Number.isFinite(value)) return "-";
  const absolute = Math.abs(value);
  const sign = value < 0 ? "-" : signed ? "+" : "";
  let body: string;
  if (absolute >= 1e9) body = `${(absolute / 1e9).toFixed(2)}B`;
  else if (absolute >= 1e6) body = `${(absolute / 1e6).toFixed(2)}M`;
  else if (absolute >= 1e3) body = `${(absolute / 1e3).toFixed(1)}K`;
  else body = `${absolute.toFixed(0)}`;
  return `${sign}$${body}`;
}

function fmtTime(iso: string): string {
  return formatTime(iso);
}

const TOOLTIP_STYLE: CSSProperties = {
  backgroundColor: "var(--card)",
  border: "1px solid var(--border)",
  borderRadius: "8px",
  fontSize: "12px",
  color: "var(--card-foreground)",
  padding: "8px 10px",
};

function sentimentColor(label?: string): string {
  if (label === "Bullish") return GREEN;
  if (label === "Bearish") return RED;
  return AMBER;
}

function useApiQuery<TData>(
  api: ApiFn,
  path: string,
  pollMs: number | undefined,
  cachedData: TData | undefined,
  onData: (data: TData) => void,
): { data: TData | null; loading: boolean } {
  const [data, setData] = useState<TData | null>(() => cachedData ?? null);
  const [loading, setLoading] = useState(!cachedData);
  const cachedDataRef = useRef(cachedData);
  const onDataRef = useRef(onData);

  useEffect(() => {
    cachedDataRef.current = cachedData;
    setData(cachedData ?? null);
    setLoading(!cachedData);
  }, [cachedData, path]);

  useEffect(() => {
    onDataRef.current = onData;
  }, [onData]);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        const response = await api<TData>("GET", path, undefined, { silent: true });
        if (!cancelled) {
          setData(response);
          onDataRef.current(response);
        }
      } catch {
        if (!cancelled && cachedDataRef.current == null) setData(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    void load();
    if (!pollMs) {
      return () => {
        cancelled = true;
      };
    }

    const timer = window.setInterval(() => {
      void load();
    }, pollMs);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [api, path, pollMs]);

  return { data, loading };
}

interface TipItem {
  dataKey?: string;
  value?: number | null;
}

function WaveTooltip({
  active,
  payload,
  label,
}: {
  active?: boolean;
  payload?: TipItem[];
  label?: string;
}) {
  if (!active || !payload?.length) return null;
  const call = payload.find((item) => item.dataKey === "cum_call_prem")?.value;
  const put = payload.find((item) => item.dataKey === "cum_put_prem")?.value;
  const price = payload.find((item) => item.dataKey === "underlying_price")?.value;
  return (
    <div style={TOOLTIP_STYLE}>
      <div className="mb-1 font-semibold">{fmtTime(String(label))}</div>
      {call != null ? (
        <div className="flex justify-between gap-3">
          <span style={{ color: GREEN }}>Calls</span>
          <span className="font-mono">{fmtCompact(Number(call), true)}</span>
        </div>
      ) : null}
      {put != null ? (
        <div className="flex justify-between gap-3">
          <span style={{ color: RED }}>Puts</span>
          <span className="font-mono">{fmtCompact(Number(put), true)}</span>
        </div>
      ) : null}
      {price != null && Number(price) > 0 ? (
        <div className="flex justify-between gap-3">
          <span style={{ color: AMBER }}>Price</span>
          <span className="font-mono">${Number(price).toFixed(2)}</span>
        </div>
      ) : null}
    </div>
  );
}

function PremiumChart({
  buckets,
  premiumDomain,
  height,
}: {
  buckets: Bucket[];
  premiumDomain?: [number, number];
  height: number | `${number}%`;
}) {
  return (
    <ResponsiveContainer width="100%" height={height}>
      <ComposedChart data={buckets} margin={{ top: 8, right: 4, left: 0, bottom: 0 }}>
        <CartesianGrid strokeDasharray="3 3" stroke={BORDER} />
        <XAxis dataKey="bucket_ts" tick={{ fill: MUTED, fontSize: 10 }} tickFormatter={fmtTime} minTickGap={36} />
        <YAxis yAxisId="premium" domain={premiumDomain} tick={{ fill: MUTED, fontSize: 10 }} tickFormatter={(value: number) => fmtCompact(value)} width={56} />
        <YAxis yAxisId="price" orientation="right" domain={["auto", "auto"]} tick={{ fill: MUTED, fontSize: 10 }} tickFormatter={(value: number) => `$${value.toFixed(0)}`} width={48} />
        <Tooltip content={<WaveTooltip />} />
        <ReferenceLine yAxisId="premium" y={0} stroke={BORDER} />
        <Line yAxisId="premium" type="monotone" dataKey="cum_call_prem" stroke={GREEN} strokeWidth={2} dot={false} isAnimationActive={false} />
        <Line yAxisId="premium" type="monotone" dataKey="cum_put_prem" stroke={RED} strokeWidth={2} dot={false} isAnimationActive={false} />
        <Line yAxisId="price" type="monotone" dataKey="underlying_price" stroke={AMBER} strokeWidth={1.5} dot={false} isAnimationActive={false} />
      </ComposedChart>
    </ResponsiveContainer>
  );
}
function PremiumFlowMetrics({
  latestBucket,
  size = "card",
}: {
  latestBucket: Bucket | null;
  size?: "card" | "dialog";
}) {
  const labelClass = size === "dialog"
    ? "font-mono text-[10px] uppercase tracking-wide text-muted-foreground"
    : "font-mono text-[9px] uppercase tracking-wide text-muted-foreground";
  const valueClass = size === "dialog"
    ? "font-mono text-xl font-semibold leading-tight"
    : "font-mono text-base font-semibold leading-tight";

  return (
    <div className="flex flex-wrap items-end gap-4">
      <div>
        <div className={labelClass}>Net Call Flow</div>
        <div className={valueClass} style={{ color: GREEN }}>
          {latestBucket ? fmtCompact(latestBucket.cum_call_prem, true) : "-"}
        </div>
      </div>
      <div>
        <div className={labelClass}>Net Put Flow</div>
        <div className={valueClass} style={{ color: RED }}>
          {latestBucket ? fmtCompact(latestBucket.cum_put_prem, true) : "-"}
        </div>
      </div>
    </div>
  );
}

function GexStrikeChart({
  gex,
  zeroGammaStrike,
  height,
}: {
  gex: { strike: number; gex: number }[];
  zeroGammaStrike?: number | null;
  height: number | `${number}%`;
}) {
  return (
    <ResponsiveContainer width="100%" height={height}>
      <BarChart data={gex} margin={{ top: 4, right: 4, left: 0, bottom: 0 }}>
        <CartesianGrid strokeDasharray="3 3" stroke={BORDER} />
        <XAxis dataKey="strike" tick={{ fill: MUTED, fontSize: 10 }} minTickGap={20} />
        <YAxis tick={{ fill: MUTED, fontSize: 10 }} tickFormatter={(value: number) => fmtCompact(value)} width={56} />
        <Tooltip
          contentStyle={TOOLTIP_STYLE}
          formatter={(value: number) => [fmtCompact(Number(value), true), "GEX"]}
          labelFormatter={(label) => `Strike ${label}`}
        />
        <ReferenceLine y={0} stroke={BORDER} />
        {zeroGammaStrike != null ? (
          <ReferenceLine x={zeroGammaStrike} stroke={AMBER} strokeDasharray="4 2" />
        ) : null}
        <Bar dataKey="gex" isAnimationActive={false}>
          {gex.map((entry, index) => (
            <Cell key={`${entry.strike}-${index}`} fill={entry.gex >= 0 ? GREEN : RED} />
          ))}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}
function Stat({
  label,
  value,
  color,
  hint,
}: {
  label: string;
  value: string;
  color?: string;
  hint?: string;
}) {
  return (
    <div className="rounded-md border bg-background/40 px-3 py-2">
      <div className="text-[10px] uppercase tracking-wide text-muted-foreground">
        {label}
      </div>
      <div
        className="font-mono text-base font-semibold"
        style={color ? { color } : undefined}
      >
        {value}
      </div>
      {hint ? <div className="text-[10px] text-muted-foreground">{hint}</div> : null}
    </div>
  );
}

function WavesColumnSkeleton({ symbol }: { symbol: string }) {
  return (
    <div className="space-y-4">
      <SkeletonStyles />
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-3">
          <h2 className="text-base font-semibold">{symbol}</h2>
          <SkeletonText width={72} height={14} />
          <Skeleton width={74} height={22} borderRadius={999} />
        </div>
        <SkeletonText width={116} height={11} />
      </div>

      <Card>
        <CardContent className="px-4 pb-2 pt-4">
          <div className="mb-2 flex items-center justify-between gap-4">
            <h3 className="text-sm font-semibold">Cumulative Net Premium</h3>
            <div className="flex items-center gap-3">
              <SkeletonText width={32} height={11} />
              <SkeletonText width={28} height={11} />
              <SkeletonText width={30} height={11} />
            </div>
          </div>
          <div className="rounded-2xl border border-border bg-secondary/40 p-3">
            <SkeletonChart height={236} style={{ borderRadius: 14 }} />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="px-4 pb-2 pt-4">
          <div className="mb-2 flex items-center justify-between gap-4">
            <h3 className="text-sm font-semibold">0DTE GEX by Strike</h3>
            <SkeletonText width={118} height={11} />
          </div>
          <div className="rounded-2xl border border-border bg-secondary/40 p-3">
            <SkeletonChart height={156} style={{ borderRadius: 14 }} />
          </div>
          <div className="mt-3 grid gap-2">
            <SkeletonText width={92} height={10} />
            <SkeletonChart height={70} style={{ borderRadius: 10 }} />
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        {Array.from({ length: 4 }).map((_, index) => (
          <Card key={index}>
            <CardContent className="p-3">
              <div className="mb-3 flex items-center gap-2">
                <Skeleton width={14} height={14} borderRadius={4} />
                <SkeletonText width={index === 3 ? 72 : 104} height={12} />
              </div>
              {index === 0 ? (
                <div className="grid place-items-center py-2">
                  <Skeleton width={132} height={66} borderRadius={66} />
                  <SkeletonText width={82} height={14} style={{ marginTop: 10 }} />
                </div>
              ) : (
                <div className="grid gap-2">
                  <div className="rounded-md border bg-background/40 px-3 py-2">
                    <SkeletonText width={68} height={10} />
                    <SkeletonText width={92} height={16} style={{ marginTop: 8 }} />
                  </div>
                  <div className="rounded-md border bg-background/40 px-3 py-2">
                    <SkeletonText width={76} height={10} />
                    <SkeletonText width={58} height={16} style={{ marginTop: 8 }} />
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      <Card>
        <CardContent className="p-4">
          <SkeletonText width={128} height={11} />
          <div className="mt-4 grid gap-2">
            <SkeletonText width="96%" height={12} />
            <SkeletonText width="88%" height={12} />
            <SkeletonText width="64%" height={12} />
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
export default function WavesColumn({
  symbol,
  date,
  isLive,
  api,
}: {
  symbol: string;
  date: string;
  isLive: boolean;
  api: ApiFn;
}) {
  const { wavesOverview, setWavesOverview } = useAppContext();
  const today = todayInTz();
  const dateQuery = isLive ? "" : `&date=${date}`;
  const poll = isLive ? 300000 : undefined;
  const summaryPath = `/api/waves/summary?symbol=${symbol}${dateQuery}`;
  const intradayPath = `/api/waves/intraday?symbol=${symbol}${dateQuery}`;
  const cachedSummary = wavesOverview.summaryByKey[summaryPath] as Summary | undefined;
  const cachedIntraday = wavesOverview.intradayByKey[intradayPath] as Intraday | undefined;
  const { data: summary, loading: summaryLoading } = useApiQuery<Summary>(
    api,
    summaryPath,
    poll,
    cachedSummary,
    (response) => {
      setWavesOverview((current) => ({
        ...current,
        summaryByKey: { ...current.summaryByKey, [summaryPath]: response },
      }));
    },
  );
  const { data: intraday, loading: intradayLoading } = useApiQuery<Intraday>(
    api,
    intradayPath,
    poll,
    cachedIntraday,
    (response) => {
      setWavesOverview((current) => ({
        ...current,
        intradayByKey: { ...current.intradayByKey, [intradayPath]: response },
      }));
    },
  );

  const buckets = intraday?.buckets ?? [];
  const cards = summary?.cards;
  const gex = summary?.gex_profile ?? [];
  const noData = summary?.status === "no_data";
  const effectiveDate = summary?.date ?? intraday?.date ?? date;
  const effectiveIsLive = isLive && effectiveDate === date && !noData;
  const latestBucket = buckets.length ? buckets[buckets.length - 1] : null;
  const [premiumDialogOpen, setPremiumDialogOpen] = useState(false);
  const [premiumDialogChartReady, setPremiumDialogChartReady] = useState(false);
  const [gexDialogOpen, setGexDialogOpen] = useState(false);
  const [gexDialogChartReady, setGexDialogChartReady] = useState(false);

  const premiumDomain = useMemo(() => {
    if (!buckets.length) return undefined as undefined | [number, number];
    let low = 0;
    let high = 0;
    for (const bucket of buckets) {
      low = Math.min(low, bucket.cum_call_prem, bucket.cum_put_prem);
      high = Math.max(high, bucket.cum_call_prem, bucket.cum_put_prem);
    }
    const pad = Math.max(Math.abs(high - low) * 0.1, 1e6);
    return [Math.floor(low - pad), Math.ceil(high + pad)] as [number, number];
  }, [buckets]);

  useEffect(() => {
    if (!premiumDialogOpen) {
      setPremiumDialogChartReady(false);
      return;
    }

    const frame = window.requestAnimationFrame(() => {
      setPremiumDialogChartReady(true);
    });

    return () => window.cancelAnimationFrame(frame);
  }, [premiumDialogOpen]);
  useEffect(() => {
    if (!gexDialogOpen) {
      setGexDialogChartReady(false);
      return;
    }

    const frame = window.requestAnimationFrame(() => {
      setGexDialogChartReady(true);
    });

    return () => window.cancelAnimationFrame(frame);
  }, [gexDialogOpen]);
  const summaryColor = sentimentColor(cards?.sentiment);
  const netGex = summary?.net_gex ?? 0;
  const initialLoading = !summary && !intraday && (summaryLoading || intradayLoading);

  if (initialLoading) {
    return <WavesColumnSkeleton symbol={symbol} />;
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-3">
          <h2 className="text-base font-semibold">{symbol}</h2>
          {summary?.underlying_price ? (
            <span className="font-mono text-sm text-muted-foreground">
              ${summary.underlying_price.toFixed(2)}
            </span>
          ) : null}
          {cards?.sentiment ? (
            <span
              className="rounded-full px-2 py-0.5 text-xs font-semibold"
              style={{ color: summaryColor, backgroundColor: `${summaryColor}22` }}
            >
              {cards.sentiment.toUpperCase()}
            </span>
          ) : null}
        </div>
        <div className="flex items-center gap-2 text-[11px] text-muted-foreground">
          {effectiveIsLive ? (
            <span className="flex items-center gap-1 text-emerald-500">
              <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-emerald-500" />
              Live
            </span>
          ) : null}
          {summary?.updated_at ? <span>{formatUpdatedTimestamp(summary.updated_at, effectiveDate, today)}</span> : null}
        </div>
      </div>

      {noData ? (
        <Card>
          <CardContent className="py-12 text-center text-sm text-muted-foreground">
            No Waves data recorded for {symbol} on {effectiveDate}.
            <div className="mt-1 text-xs">
              History starts after the Waves engine records a trading day.
            </div>
          </CardContent>
        </Card>
      ) : (
        <>
          <Card>
            <CardContent className="px-5 pb-2">
              <div className="mb-2 flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <h3 className="text-sm font-semibold">Cumulative Net Premium</h3>
                  <div className="mt-1">
                    <PremiumFlowMetrics latestBucket={latestBucket} />
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <div className="flex items-center gap-3 text-[11px]">
                    <span style={{ color: GREEN }}>Calls</span>
                    <span style={{ color: RED }}>Puts</span>
                    <span style={{ color: AMBER }}>Price</span>
                  </div>
                  <Dialog open={premiumDialogOpen} onOpenChange={setPremiumDialogOpen}>
                    <DialogTrigger asChild>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon-xs"
                        className="text-muted-foreground hover:text-foreground"
                        aria-label={`Expand ${symbol} cumulative net premium chart`}
                        title="Expand chart"
                      >
                        <Maximize2 className="h-3.5 w-3.5" />
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="grid h-[80vh] max-h-[80vh] w-[80vw] max-w-[80vw] grid-rows-[auto_minmax(0,1fr)] gap-4 p-5" style={{ width: "80vw", maxWidth: "80vw", height: "80vh", maxHeight: "80vh" }} aria-describedby={`${symbol}-premium-chart-description`}>
                      <DialogHeader className="pr-8">
                        <DialogTitle>{symbol} Cumulative Net Premium</DialogTitle>
                        <DialogDescription id={`${symbol}-premium-chart-description`}>
                          Net call flow, net put flow, and underlying price in Eastern Time.
                        </DialogDescription>
                        <PremiumFlowMetrics latestBucket={latestBucket} size="dialog" />
                      </DialogHeader>
                      <div className="min-h-0">
                        {premiumDialogChartReady ? (
                          <PremiumChart buckets={buckets} premiumDomain={premiumDomain} height="100%" />
                        ) : (
                          <div className="h-full rounded-md bg-muted/40" />
                        )}
                      </div>
                    </DialogContent>
                  </Dialog>
                </div>
              </div>
              <PremiumChart buckets={buckets} premiumDomain={premiumDomain} height={260} />
              {buckets.length === 0 ? (
                <div className="py-6 text-center text-xs text-muted-foreground">
                  Awaiting intraday data...
                </div>
              ) : null}
            </CardContent>
          </Card>

          <Card>
            <CardContent className="px-5 pb-2">
              <div className="mb-2 flex items-center justify-between gap-3 pb-2">
                <h3 className="text-sm font-semibold">0DTE GEX by Strike</h3>
                <div className="flex items-center gap-2">
                  <div className="flex items-center gap-3 text-[11px] text-muted-foreground">
                    <span>
                      Net:{" "}
                      <span className="font-mono font-semibold" style={{ color: netGex >= 0 ? GREEN : RED }}>
                        {fmtCompact(netGex, true)}
                      </span>
                    </span>
                    {summary?.zero_gamma_strike != null ? (
                      <span>
                        Flip: <span className="font-mono font-semibold text-foreground">{summary.zero_gamma_strike}</span>
                      </span>
                    ) : null}
                  </div>
                  <Dialog open={gexDialogOpen} onOpenChange={setGexDialogOpen}>
                    <DialogTrigger asChild>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon-xs"
                        className="text-muted-foreground hover:text-foreground"
                        aria-label={`Expand ${symbol} 0DTE GEX by strike chart`}
                        title="Expand chart"
                      >
                        <Maximize2 className="h-3.5 w-3.5" />
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="grid h-[80vh] max-h-[80vh] w-[80vw] max-w-[80vw] grid-rows-[auto_minmax(0,1fr)] gap-4 p-5" style={{ width: "80vw", maxWidth: "80vw", height: "80vh", maxHeight: "80vh" }} aria-describedby={`${symbol}-gex-chart-description`}>
                      <DialogHeader className="pr-8">
                        <DialogTitle>{symbol} 0DTE GEX by Strike</DialogTitle>
                        <DialogDescription id={`${symbol}-gex-chart-description`}>
                          Gamma exposure by strike with zero-gamma flip level highlighted when available.
                        </DialogDescription>
                        <div className="flex items-center gap-3 text-[11px] text-muted-foreground">
                          <span>
                            Net:{" "}
                            <span className="font-mono font-semibold" style={{ color: netGex >= 0 ? GREEN : RED }}>
                              {fmtCompact(netGex, true)}
                            </span>
                          </span>
                          {summary?.zero_gamma_strike != null ? (
                            <span>
                              Flip: <span className="font-mono font-semibold text-foreground">{summary.zero_gamma_strike}</span>
                            </span>
                          ) : null}
                        </div>
                      </DialogHeader>
                      <div className="min-h-0">
                        {gexDialogChartReady ? (
                          <GexStrikeChart gex={gex} zeroGammaStrike={summary?.zero_gamma_strike} height="100%" />
                        ) : (
                          <div className="h-full rounded-md bg-muted/40" />
                        )}
                      </div>
                    </DialogContent>
                  </Dialog>
                </div>
              </div>
              <GexStrikeChart gex={gex} zeroGammaStrike={summary?.zero_gamma_strike} height={180} />
              {gex.length === 0 ? (
                <div className="py-4 text-center text-xs text-muted-foreground">
                  No 0DTE gamma data for this session.
                </div>
              ) : null}
              {buckets.length > 0 ? (
                <div className="mt-1">
                  <div className="mb-1 text-[10px] uppercase tracking-wide text-muted-foreground">
                    Net GEX Intraday
                  </div>
                  <ResponsiveContainer width="100%" height={70}>
                    <ComposedChart data={buckets} margin={{ top: 2, right: 4, left: 0, bottom: 0 }}>
                      <XAxis dataKey="bucket_ts" hide />
                      <YAxis hide domain={["auto", "auto"]} />
                      <Tooltip
                        contentStyle={TOOLTIP_STYLE}
                        formatter={(value: number) => [fmtCompact(Number(value), true), "Net GEX"]}
                        labelFormatter={(label) => fmtTime(String(label))}
                      />
                      <ReferenceLine y={0} stroke={BORDER} />
                      <Line type="monotone" dataKey="net_gex" stroke={ACCENT} strokeWidth={2} dot={false} isAnimationActive={false} />
                    </ComposedChart>
                  </ResponsiveContainer>
                </div>
              ) : null}
            </CardContent>
          </Card>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Card>
              <CardContent className="p-3">
                <div className="mb-1 flex items-center gap-1 text-xs font-semibold">
                  <Activity className="h-3.5 w-3.5" /> Market Sentiment
                </div>
                <div className="relative h-[90px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <RadialBarChart
                      innerRadius="72%"
                      outerRadius="100%"
                      data={[{ value: cards?.sentiment_value ?? 50, fill: summaryColor }]}
                      startAngle={180}
                      endAngle={0}
                    >
                      <PolarAngleAxis type="number" domain={[0, 100]} angleAxisId={0} tick={false} />
                      <RadialBar background dataKey="value" cornerRadius={6} isAnimationActive={false} />
                    </RadialBarChart>
                  </ResponsiveContainer>
                  <div className="absolute inset-x-0 bottom-1 text-center">
                    <div className="text-sm font-bold" style={{ color: summaryColor }}>
                      {(cards?.sentiment ?? "-").toUpperCase()}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="space-y-2 p-3">
                <div className="flex items-center gap-1 text-xs font-semibold" style={{ color: GREEN }}>
                  <TrendingUp className="h-3.5 w-3.5" /> Call Side
                </div>
                <Stat label="Call Flow" value={fmtCompact(cards?.call_flow_usd ?? 0)} color={GREEN} />
                <Stat label="Put/Call Vol" value={(cards?.put_call_ratio ?? 0).toFixed(2)} />
              </CardContent>
            </Card>

            <Card>
              <CardContent className="space-y-2 p-3">
                <div className="flex items-center gap-1 text-xs font-semibold" style={{ color: RED }}>
                  <TrendingDown className="h-3.5 w-3.5" /> Put Side
                </div>
                <Stat label="Put Flow" value={fmtCompact(cards?.put_flow_usd ?? 0)} color={RED} />
                <Stat label="Sweeps" value={`${(cards?.sweeps_pct ?? 0).toFixed(1)}%`} hint="Intermarket sweep orders" />
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-3">
                <div className="mb-2 flex items-center gap-1 text-xs font-semibold">
                  <Flame className="h-3.5 w-3.5" /> Top Flow
                </div>
                {cards?.top_flow?.length ? (
                  <div className="space-y-1">
                    {cards.top_flow.slice(0, 5).map((trade) => (
                      <div key={`${trade.ticker}-${trade.premium}`} className="flex items-center justify-between text-[11px]">
                        <span className="flex items-center gap-1">
                          <span className="font-mono" style={{ color: trade.type === "call" ? GREEN : RED }}>
                            {trade.type === "call" ? "C" : "P"}
                            {trade.strike}
                          </span>
                          {trade.is_sweep ? <Zap className="h-3 w-3 text-amber-500" /> : null}
                        </span>
                        <span className="font-mono">{fmtCompact(trade.premium)}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="py-3 text-center text-[11px] text-muted-foreground">
                    Awaiting data
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardContent className="p-4">
              <div className="mb-1 flex items-center justify-between">
                <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  Trader Read - {symbol}
                </div>
                {summary?.is_approximation ? (
                  <span
                    className="rounded bg-amber-500/10 px-1.5 py-0.5 text-[10px] text-amber-600"
                    title="Net-premium curve is approximated from 5-minute volume deltas and trade location."
                  >
                    Approximation
                  </span>
                ) : null}
              </div>
              <p className="text-sm leading-relaxed text-foreground">
                {summary?.ai_summary || (summaryLoading ? "Generating read..." : "No summary available yet.")}
              </p>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
